import React, { Component } from 'react';
import { connect } from 'react-redux';
import { setWindowSize } from './actions';
import './css/index.scss';
import { activeCluster, metroClusters, activeSchool } from './modules/get-data';
import { Q } from './modules/helpers';
import 'iframe-resizer';

import ClusterMap from './components/map';
import ClusterProjections from './components/cluster-projections';
import StudentEnrolment from './components/students';
import ClassroomTypes from './components/classrooms';
import AvgSpaceComparison from './components/AvgSpaceComparison';
import AvgPlayspace from './components/playspace';
import AvgClassSpace from './components/classroom-space';
import AllSchoolsTable from './components/all-schools';
import throttle from './modules/throttle';
import filter from './modules/filter-data';

import PrevNext from './components/prev-next';
import clusterAggregate from './components/singleClusterProjectionsLineGraph/clusterAggregate';

function getProjectionSchools() {
  const data = activeCluster.schools.map(d => d).filter(d => {
    return Array.isArray(d.projections_percentage);
  });
  // Add the cluster aggregate as "another school"
  const cluster = clusterAggregate(data, activeCluster);
  data.push(cluster);
  return data;
}

const components = [
  {
    name: 'cluster-map',
    component: <ClusterMap data={activeCluster} />,
    className: 'cluster-map-section',
  },
  {
    name: 'projection',
    component: (
      <ClusterProjections
        data={activeCluster}
        school={activeSchool}
        isMetro={metroClusters.indexOf(activeCluster.uid) !== -1}
        chartName="projection"
      />
    ),
    title: 'Projected change in enrolments',
    sub: 'last updated: June 2017',
    controls: <PrevNext chart="projection" schools={getProjectionSchools()} />,
  },
  {
    name: 'skip',
    component: (
      <a href="#summary-table">{`Skip to summary of ${activeCluster.name} statistics.`}</a>
    ),
    className: 'a11y',
  },
  {
    name: 'student-enrolment',
    component: (
      <StudentEnrolment chartName="student-enrolment" data={activeCluster} />
    ),
    title: 'Number of enrolments',
    hr: true,
    controls: (
      <PrevNext
        chart="student-enrolment"
        schools={filter.studentEnrolment(activeCluster.schools)}
      />
    ),
  },
  {
    name: 'classrooms',
    component: <ClassroomTypes chartName="classrooms" data={activeCluster} />,
    title: 'Classrooms',
    hr: true,
    controls: (
      <PrevNext
        chart={'classrooms'}
        schools={filter.studentClassrooms(activeCluster.schools)}
      />
    ),
  },
  {
    name: 'playspace',
    title: 'Play space per student',
    hr: true,
    component: <AvgPlayspace chartName={'playspace'} data={activeCluster} />,
    controls: (
      <PrevNext
        chart={'playspace'}
        schools={filter.playspace(activeCluster.schools)}
      />
    ),
  },
  {
    name: 'classroom-space',
    title: 'Classroom space per student',
    hr: true,
    component: (
      <AvgClassSpace chartName="classroom-space" data={activeCluster} />
    ),
    controls: (
      <PrevNext
        chart={'classroom-space'}
        schools={filter.classSpace(activeCluster.schools)}
      />
    ),
  },
  {
    name: 'playspace-comparison',
    component: (
      <AvgSpaceComparison
        data={activeCluster}
        chartName="playspace-comparison"
      />
    ),
    controls: (
      <PrevNext
        chart="playspace-comparison"
        schools={filter.playspace(activeCluster.schools)}
      />
    ),
  },
  {
    name: 'schools-table',
    component: <AllSchoolsTable data={activeCluster} school={activeSchool} />,
    title: `Summary of ${activeCluster.name} schools group`,
    className: 'bg-dark',
  },
];

class App extends Component {
  constructor() {
    super();
    this.state = {
      selectedSchool: activeCluster.schools[0].uid,
    };
    this.handleWindowResize = throttle(this.handleWindowResize.bind(this), 500);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);

    // Make sure that the arrows on the enrolment graph are in the right position.
    // First load has them in the wrong position
    window.onload = _ => {
      window.dispatchEvent(new Event('resize'));
    };
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleWindowResize() {
    this.props.setWindowSize();
  }

  makeSection(chart) {
    return (
      <section
        key={`section-${chart.name}`}
        className={`section ${chart.className || ''}`}
      >
        <div className="wrapper">
          {chart.title &&
            <h3 className="mbm">
              {chart.title}
            </h3>}
          {chart.sub &&
            <p className="section-sup">
              {chart.sub}
            </p>}
          {chart.hr && <hr />}
          {chart.component}
          {chart.controls}
        </div>
      </section>
    );
  }

  renderCharts(charts) {
    return charts.map(chartName => {
      const chart = components.find(d => d.name === chartName);
      if (!chart && chartName !== 'playspace-responsive') {
        console.error(chartName, ' is not a valid chart');
        return null;
      }
      if (chartName === 'playspace-responsive') {
        const charts = this.props.isMobile
          ? ['playspace', 'classroom-space']
          : ['playspace-comparison'];

        return (
          <div>
            {charts.map(chartName => {
              const chart = components.find(d => d.name === chartName);
              return this.makeSection(chart);
            })}
          </div>
        );
      }
      return this.makeSection(chart);
    });
  }

  render() {
    const defaultCharts = this.props.isMobile
      ? [
          'cluster-map',
          'projection',
          'skip',
          'student-enrolment',
          'classrooms',
          'playspace',
          'classroom-space',
          'schools-table',
        ]
      : [
          'cluster-map',
          'projection',
          'skip',
          'student-enrolment',
          'classrooms',
          'playspace-comparison',
          'schools-table',
        ];

    const charts = Q.charts ? Q.charts.split(',') : defaultCharts;

    return (
      <div className="chart-wrapper">
        {this.renderCharts(charts)}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isMobile: state.ui.isMobile,
});

const mapDispatchToProps = dispatch => ({
  setWindowSize: p => dispatch(setWindowSize(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);



// WEBPACK FOOTER //
// ./src/App.js